<template>
    <div>
        <!-- Floating Chat Window -->
        <div
            v-if="!isMinimized && chats.length > 0"
            ref="chatWindow"
            :style="chatWindowStyle"
            class="chat-window"
        >
            <!-- Chat Header -->
            <div
                class="chat-header"
                @mousedown="startDrag"
            >
                <template v-if="selectedChatIndex != null && chats[selectedChatIndex]">
                    <div>
                        <strong>Задание для поставки {{ chats[selectedChatIndex].name }}</strong>
                        <template v-if="chats[selectedChatIndex].comment">
                            <div class="task-msg">
                                <b-icon icon="info-circle" class="mx-1" scale="1" variant="link"></b-icon>
                                {{ chats[selectedChatIndex].comment }}
                            </div>
                        </template>
                    </div>
                </template>
                <template v-else>
                    <strong>Задания по поставкам</strong>
                </template>
                <div class="chat-actions">
                    <b-button
                        v-if="chats[selectedChatIndex] && chats[selectedChatIndex].order_id"
                        size="sm"
                        variant="outline-secondary"
                        class="mr-2"
                        @click="chats[selectedChatIndex].order_service_id ? openOrderSidebar(chats[selectedChatIndex].order_id, false, '23') : openOrderSidebar(chats[selectedChatIndex].order_id)"
                    >
                        В поставку
                    </b-button>
                    <b-button
                        size="sm"
                        variant="outline-secondary"
                        :disabled="isCompletingTask"
                        @click="refreshData()"
                    >
                        <b-icon
                            icon="arrow-repeat"
                            scale="1.2"
                            aria-label="Reload"
                            :animation="isDataRefreshing ? 'spin' : ''"
                        />
                    </b-button>

                    <b-button
                        size="sm"
                        variant="outline-success"
                        class="ml-2"
                        title="Завершить задачу"
                        :disabled="isCompletingTask"
                        @click="closeTask()"
                    >
                        <b-icon 
                            v-if="!isCompletingTask" 
                            icon="check" 
                            scale="1.8" 
                        ></b-icon>
                        <b-icon
                            v-if="isCompletingTask"
                            icon="arrow-repeat"
                            scale="1.2"
                            aria-label="Reload"
                            :animation="isDataRefreshing ? 'spin' : ''"
                        />
                    </b-button>
                    <!-- Maximize Button -->
                    <!-- <b-button
                        variant="link"
                        style="color: #000;"
                        :title="isMaximized ? 'Свернуть' : 'Развернуть'"
                        @click="toggleMaximize"
                    >
                        <b-icon
                            :icon="isMaximized ? 'fullscreen-exit' : 'fullscreen'"
                            scale="1.1"
                        />
                    </b-button> -->
                    <!-- Close Button -->
                    <b-button
                        variant="link"
                        style="color: #000;"
                        @click="closeChat"
                    >
                        <b-icon
                            icon="x"
                            scale="1.2"
                        />
                    </b-button>
                </div>
            </div>
  
            <!-- Chat Body -->
            <div class="chat-body" :class="isDataRefreshing ? 'chat-body-loading':''">
                <template v-if="isDataRefreshing">
                    <div>
                        <div class="text-center text-success mb-3">
                            <b-spinner variant="success"></b-spinner>
                        </div>
                        <div class="text-center">Загружаем поставки...</div>
                    </div>
                </template>
                <template v-else>
                    <!-- Left Side: Chat List -->
                    <div
                        ref="chatList"
                        class="chat-list"
                    >
                        <b-list-group>
                            <b-list-group-item
                                v-for="(chat, index) in chats"
                                :key="`chat_head_${index}`"
                                class="chat-name-item"
                                :class="{ active: selectedChatIndex === index }"
                                @click="selectChat(index)"
                                :title="chat.name"
                            >
                                <span class="chat-name">{{ chat.name }}</span>
                            </b-list-group-item>
                        </b-list-group>
                    </div>
    
                    <!-- Right Side: Selected Chat Messages -->
                    <div class="chat-messages d-flex flex-column h-100">
                        <OrderServiceComments
                            v-if="selectedOrderServiceId"
                            :key="`loaded_comments_${selectedOrderServiceId}`"
                            :isNeedHeader="false"
                            :isFullHeightEmbedded="true"
                            :orderServiceId="selectedOrderServiceId"
                        />
                        <!-- @commentSubmitted="closeTask" -->
                        <div 
                            v-if="isCompletingTask"
                            style="position: absolute; width: 100%; height: 100%; top: 0; left: 0;"
                        >
                            <b-overlay 
                                :show="isCompletingTask"
                                no-wrap
                            />
                        </div>
                    </div>
                </template>        
            </div>
  
            <!-- Resize Handle -->
            <div
                class="resize-handle"
                @mousedown="startResize"
            />
        </div>
  
        <!-- Chat Toggle Button (When Minimized) -->
        <div
            v-if="chats.length > 0"
            ref="toggleButton"
            :style="toggleButtonStyle"
            class="chat-toggle-button"
            @mousedown="startToggleButtonDrag"
            @click="toggleMinimize"
        >
            <!-- <b-icon icon="chat" /> -->
            <div 
                v-if="!isDataRefreshing"
                class="truck-icon-container"
            >
                <b-icon icon="truck" />
            </div>

            <b-icon
                v-if="isDataRefreshing"
                icon="arrow-repeat"
                scale="1.2"
                aria-label="Reload"
                animation="spin"
            />
        </div>
    </div>
</template>

<script>
import { mapGetters } from 'vuex';
import { 
    API_QUICK_TASKS
} from '@/constants';

import { openOrderSidebar } from '@/mixins/openOrderSidebar';
import OrderServiceComments from "@/components/orders/OrderServiceComments";
  
export default {
    components: {
        OrderServiceComments
    },
    mixins: [
        openOrderSidebar
    ],
    data() {
        return {
            data: [],
            chats: [],
            selectedChatIndex: null,
            selectedOrderServiceId: null,
            refreshInterval: null,
            isDataRefreshing: false,
            isCompletingTask: false,
            isMinimized: false,
            isDragging: false,
            isResizing: false,
            isToggleButtonDragging: false,
            isMaximized: false, // Track maximize state
            chatWindowStyle: {
                position: 'fixed',
                top: '80px',
                left: '214px',
                width: '800px',
                height: '600px',
                zIndex: 1000,
            },
            toggleButtonStyle: {
                position: 'fixed',
                top: '20px',
                left: '20px',
                zIndex: 999,
            },
            chatListWidth: 0, // Track the width of the chat list
            originalChatWindowStyle: null, // Store the original style for restoring
        };
    },
    computed: {
        ...mapGetters('auth', ['currentUser'])
    },
    created() {
        // Load saved chat window and toggle button styles from localStorage
        const savedChatWindowStyle = localStorage.getItem('chatWindowStyle');
        const savedToggleButtonStyle = localStorage.getItem('toggleButtonStyle');
  
        if (savedChatWindowStyle) {
            this.chatWindowStyle = JSON.parse(savedChatWindowStyle);
        } else {
            // Default position for chat window (centered horizontally and slightly below the top)
            this.chatWindowStyle.left = `${(window.innerWidth - 800) / 2}px`; // Center horizontally
            this.chatWindowStyle.top = '80px'; // Slightly below the top
        }
  
        if (savedToggleButtonStyle) {
            this.toggleButtonStyle = JSON.parse(savedToggleButtonStyle);
        } else {
            // Default position for toggle button (bottom-right corner)
            this.toggleButtonStyle.top = `${window.innerHeight - 60}px`; // 60px from bottom
            this.toggleButtonStyle.left = `${window.innerWidth - 60}px`; // 60px from right
        }
    },
    mounted() {
        // Initialize ResizeObserver to track changes in the chat list width
        this.resizeObserver = new ResizeObserver((entries) => {
            for (let entry of entries) {
                this.chatListWidth = entry.contentRect.width;
            }
        });

        if (this.$refs.chatList) {
            this.resizeObserver.observe(this.$refs.chatList);
        }

        this.loadData();

        // Refresh data every 10 minutes (600,000 ms)
        this.refreshInterval = setInterval(() => {
            if (this.isMinimized) {
                this.refreshData();
            }
        }, 600000);

        // Корректировка положения при изменении размера окна
        window.addEventListener("resize", this.onWindowResize);
        
        // Первоначальная корректировка после загрузки страницы
        this.adjustChatWindowPosition();
        this.adjustToggleButtonPosition();
    },

    beforeUnmount() {
        if (this.resizeObserver) {
            this.resizeObserver.disconnect();
        }

        if (this.refreshInterval) {
            clearInterval(this.refreshInterval);
        }

        // Удаляем слушатель resize
        window.removeEventListener("resize", this.onWindowResize);
    },
    methods: {
        loadData() {
            if (!this.currentUser || !this.currentUser.id) {
                console.error('Ошибка: user_id отсутствует.');
                return;
            }

            this.selectedChatIndex = null;
            this.isDataRefreshing = true;

            const params = {
                user_id: this.currentUser.id,
                taskable_type: 'App\\Models\\OrderService' // Используем двойной слэш для PHP namespace
            };

            api.call('get', API_QUICK_TASKS, { params })
                .then(({ data }) => {
                    this.data = data;

                    this.chats = this.data.map(item => ({
                        id: item.id,
                        order_id: item.taskable.order.id,
                        order_service_id: item.taskable.id,
                        name: `#${item.taskable.order.id}. ${item.taskable.company.name}`,
                        comment: item.comment
                    }));

                    setTimeout(() => {
                        this.selectChat(0);
                    }, 300);
                })
                .catch((error) => {
                    this.chats = [];
                    console.error('Ошибка при загрузке данных:', error);
                })
                .finally(() => {
                    this.isDataRefreshing = false;
                });
        },

        refreshData() {
            this.isDataRefreshing = true;
            this.loadData();
        },

        selectChat(index) {
            this.selectedChatIndex = index;
            this.loadComments();
        },

        loadComments() {
            const chat = this.chats[this.selectedChatIndex];
            if (!chat) {
                this.selectedOrderServiceId = null;
                return;
            }

            this.selectedOrderServiceId = chat.order_service_id;
        },

        closeTask() {
            const taskId = this.chats[this.selectedChatIndex].id;
            this.isCompletingTask = true;

            api.call('post', `${API_QUICK_TASKS}/${taskId}/complete`)
                .then(({ data }) => {
                    this.$bvToast.toast('Через несколько секунд задача с поставкой удалится из списка задач.', {
                        title: 'Задание успешно закрыто!',
                        variant: 'success',
                        solid: true,
                    }); 
                    
                    setTimeout(() => {
                        this.isCompletingTask = false;
                        this.chats.splice(this.selectedChatIndex, 1);                    
                        this.selectChat(0);
                    }, 2500);
                })
                .catch((error) => {
                    console.error('Ошибка при закрытии задания:', error);
                })
                .finally(() => {
                });
        },

        toggleMinimize() {
            this.isMinimized = !this.isMinimized;
        },

        closeChat() {
            this.isMinimized = true;
        },

        startDrag(event) {
            this.isDragging = true;
            document.body.classList.add('no-select');
            const chatWindow = this.$refs.chatWindow;
            const offsetX = event.clientX - chatWindow.getBoundingClientRect().left;
            const offsetY = event.clientY - chatWindow.getBoundingClientRect().top;
  
            const onMouseMove = (e) => {
                if (this.isDragging) {
                    const newLeft = e.clientX - offsetX;
                    const newTop = e.clientY - offsetY;
  
                    // Prevent moving outside the screen
                    this.chatWindowStyle.left = `${Math.max(0, Math.min(newLeft, window.innerWidth - chatWindow.offsetWidth))}px`;
                    this.chatWindowStyle.top = `${Math.max(0, Math.min(newTop, window.innerHeight - chatWindow.offsetHeight))}px`;
                }
            };
  
            const onMouseUp = () => {
                this.isDragging = false;
                document.body.classList.remove('no-select');
                this.saveStyles();
                window.removeEventListener('mousemove', onMouseMove);
                window.removeEventListener('mouseup', onMouseUp);
            };
  
            window.addEventListener('mousemove', onMouseMove);
            window.addEventListener('mouseup', onMouseUp);
        },

        startResize(event) {
            this.isResizing = true;
            document.body.classList.add('no-select');
            const chatWindow = this.$refs.chatWindow;
            const startWidth = chatWindow.offsetWidth;
            const startHeight = chatWindow.offsetHeight;
            const startX = event.clientX;
            const startY = event.clientY;
  
            const onMouseMove = (e) => {
                if (this.isResizing) {
                    const newWidth = startWidth + (e.clientX - startX);
                    const newHeight = startHeight + (e.clientY - startY);
  
                    // Prevent resizing outside the screen
                    this.chatWindowStyle.width = `${Math.max(300, Math.min(newWidth, window.innerWidth - chatWindow.getBoundingClientRect().left))}px`;
                    this.chatWindowStyle.height = `${Math.max(200, Math.min(newHeight, window.innerHeight - chatWindow.getBoundingClientRect().top))}px`;
                }
            };
  
            const onMouseUp = () => {
                this.isResizing = false;
                document.body.classList.remove('no-select');
                this.saveStyles();
                window.removeEventListener('mousemove', onMouseMove);
                window.removeEventListener('mouseup', onMouseUp);
            };
  
            window.addEventListener('mousemove', onMouseMove);
            window.addEventListener('mouseup', onMouseUp);
        },

        startToggleButtonDrag(event) {
            this.isToggleButtonDragging = true;
            document.body.classList.add('no-select');
            const toggleButton = this.$refs.toggleButton;
            const offsetX = event.clientX - toggleButton.getBoundingClientRect().left;
            const offsetY = event.clientY - toggleButton.getBoundingClientRect().top;
  
            const onTbMouseMove = (e) => {
                if (this.isToggleButtonDragging) {
                    const newLeft = e.clientX - offsetX;
                    const newTop = e.clientY - offsetY;
  
                    // Prevent moving outside the screen
                    this.toggleButtonStyle.left = `${Math.max(0, Math.min(newLeft, window.innerWidth - toggleButton.offsetWidth))}px`;
                    this.toggleButtonStyle.top = `${Math.max(0, Math.min(newTop, window.innerHeight - toggleButton.offsetHeight))}px`;
                }
            };
  
            const onTbMouseUp = () => {
                this.isToggleButtonDragging = false;
                document.body.classList.remove('no-select');
                this.saveStyles();
                window.removeEventListener('mousemove', onTbMouseMove);
                window.removeEventListener('mouseup', onTbMouseUp);
            };
  
            window.addEventListener('mousemove', onTbMouseMove);
            window.addEventListener('mouseup', onTbMouseUp);
        },

        onWindowResize() {
            this.adjustChatWindowPosition();
            this.adjustToggleButtonPosition();
        },

        adjustChatWindowPosition() {
            const chatWindow = this.$refs.chatWindow;
            if (!chatWindow) return;

            const windowWidth = window.innerWidth;
            const windowHeight = window.innerHeight;
            const chatRect = chatWindow.getBoundingClientRect();

            // Проверяем и корректируем положение окна чата
            if (chatRect.right > windowWidth) {
                this.chatWindowStyle.left = `${windowWidth - chatRect.width - 15}px`;
            }
            if (chatRect.bottom > windowHeight) {
                this.chatWindowStyle.top = `${windowHeight - chatRect.height - 15}px`;
            }
            if (chatRect.left < 0) {
                this.chatWindowStyle.left = "15px";
            }
            if (chatRect.top < 0) {
                this.chatWindowStyle.top = "15px";
            }
        },

        adjustToggleButtonPosition() {
            const toggleButton = this.$refs.toggleButton;
            if (!toggleButton) return;

            const windowWidth = window.innerWidth;
            const windowHeight = window.innerHeight;
            const buttonRect = toggleButton.getBoundingClientRect();

            // Проверяем и корректируем положение кнопки
            if (buttonRect.right > windowWidth) {
                this.toggleButtonStyle.left = `${windowWidth - buttonRect.width - 15}px`;
            }
            if (buttonRect.bottom > windowHeight) {
                this.toggleButtonStyle.top = `${windowHeight - buttonRect.height - 15}px`;
            }
            if (buttonRect.left < 0) {
                this.toggleButtonStyle.left = "15px";
            }
            if (buttonRect.top < 0) {
                this.toggleButtonStyle.top = "15px";
            }
        },  

        saveStyles() {
            localStorage.setItem('chatWindowStyle', JSON.stringify(this.chatWindowStyle));
            localStorage.setItem('toggleButtonStyle', JSON.stringify(this.toggleButtonStyle));
        },

        // Maximize methods
        toggleMaximize() {
            if (!this.isMaximized) {
                this.maximize();
            } else {
                this.restore();
            }
        },

        maximize() {
            // Save the original style before maximizing
            this.originalChatWindowStyle = { ...this.chatWindowStyle };

            // Set the chat window to fill the screen with 15px padding
            this.chatWindowStyle = {
                position: 'fixed',
                top: '15px',
                left: '15px',
                width: `${window.innerWidth - 30}px`,
                height: `${window.innerHeight - 30}px`,
                zIndex: 1000,
            };

            this.isMaximized = true;
        },

        restore() {
            // Restore the original style
            if (this.originalChatWindowStyle) {
                this.chatWindowStyle = { ...this.originalChatWindowStyle };
            }

            this.isMaximized = false;
        },
    },
};
</script>

<style scoped>
.chat-window {
  background-color: white;
  border: 1px solid #ddd;
  border-radius: 8px;
  box-shadow: 0 4px 21px rgba(0, 0, 0, 0.4);
  display: flex;
  flex-direction: column;
  resize: none;
  min-width: 575px;
  min-height: 500px;
  padding-bottom: 6px;
}

.chat-header {
  padding: 10px 0px 10px 10px;
  background-color: #f8f9fa;
  border-bottom: 1px solid #ddd;
  border-top-left-radius: 8px;
  border-top-right-radius: 8px;
  cursor: move;
  display: flex;
  justify-content: space-between;
  align-items: center;
  line-height: 16px;
}

.chat-actions {
    width: 230px;
    text-align: right;
    padding-right: 7px;
}

.chat-body {
  display: flex;
  flex: 1;
  overflow: hidden;
  padding-top: 6px;
}

.chat-body-loading {
    align-items: center;
    justify-content: center;
}

.chat-name-item {
    cursor: pointer;
    padding-left: 10px;
    padding-right: 5px;
    font-size: 12px;
    line-height: 12px;
    border-right: none;
    border-left: none;
    border-radius: 0;
    overflow: hidden;
}

.chat-list {
  width: 25%;
  border-right: 1px solid #ddd;
  overflow-y: auto;
}

.chat-list::-webkit-scrollbar {
    width: 6px;
    border-radius: 6px !important;
}
.chat-list::-webkit-scrollbar-thumb {
    border-radius: 6px !important;
}

.chat-messages {
  position: relative;
  width: 75%;
  overflow-y: auto;
  padding: 10px;
}

@keyframes blink-animation {
    0% { opacity: 1; }
    50% { opacity: 0.7; }
    100% { opacity: 1; }
}

@keyframes truck-drive-loop {
    0% { transform: translateX(-30px); opacity: 0; } /* Start from outside left */
    10% { opacity: 1; } /* Fade in */
    30% { transform: translateX(0px); opacity: 1; }
    70% { transform: translateX(0px); opacity: 1; }
    100% { transform: translateX(30px); opacity: 0; } /* Reappear on left */
}

@keyframes splash-animation {
    0% { transform: scale(0); opacity: 0; } /* Invisible and small */
    50% { transform: scale(1.5); opacity: 1; } /* Quick burst effect */
    100% { transform: scale(1); opacity: 1; } /* Settle to normal size */
}

.chat-toggle-button {
    position: fixed;
    background-color: #0D6247;
    color: white;
    padding: 10px 10px;
    border-radius: 50%;
    cursor: pointer;
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
    z-index: 999;
    animation: splash-animation 0.6s ease-out, blink-animation 2.5s infinite ease-in-out;
    display: flex;
    justify-content: center;
    align-items: center;
    overflow: hidden; /* Hide truck when outside */
}

.truck-icon-container {
    animation: truck-drive-loop 4s infinite linear;
    display: flex;
    align-items: center;
}

.resize-handle {
  position: absolute;
  bottom: 0;
  right: 0;
  width: 6px;
  height: 6px;
  cursor: se-resize;
  background-color: #999999;
  border-radius: 2px;
}

.active {
  background-color: #7d7e7d36;
  color: #000;
  font-weight: bold;
  border-color: #CCC;
}

.chat-name {
  display: block;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.task-msg {
    background-color: #fcfbdb;
    border-color: #fcfacc;
    padding: 4px 5px;
    margin-top: 3px;
    margin-left: -5px;
    font-style: italic;
}
</style>